import React, {useEffect, useState} from 'react';
import {BsEye as Eye, BsEyeSlashFill as EyeSlashFill} from "react-icons/bs"
import * as Yup from 'yup';
import {
    Accordion,
    Alert,
    Button,
    Col,
    FormControl,
    FormGroup,
    FormLabel,
    InputGroup,
    Modal,
    ModalTitle,
    Row,
    Spinner,
} from "react-bootstrap";
import {ErrorMessage, Field, Form, Formik} from 'formik';
import {BsEnvelope, BsPlusLg, BsTrash} from "react-icons/bs";
import TextEditor, {sanitizeHtml} from "../../components/TextEditor";


const SaveCareUnitDialog = ({
                                saveErrorMessage,
                                onSaveClicked,
                                onUpdateClicked,
                                onCancelClicked,
                                onDeleteClicked,
                                careUnitForm,
                                loggedInUserRole,
                                isLoading,
                            }) => {
    const isUpdating = !!careUnitForm.name;

    const [confirmAction, setConfirmAction] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (saveErrorMessage) {
            setConfirmAction(null);
        }
    }, [saveErrorMessage]);

    const isAdminRole = loggedInUserRole === 'ADMIN';

    const validationSchema = Yup.object().shape({
        organizationEmail: Yup.string().email("Ogiltig e-postadress").required("Obligatoriskt fält"),
        adminEmail: isUpdating ? Yup.string() : Yup.string().email("Ogiltig e-postadress").required("Obligatoriskt fält"),
        adminEmailRepetition: isUpdating ? Yup.string() : Yup.string().oneOf([Yup.ref('adminEmail'), null], "E-postadresserna matchar inte").required("Obligatoriskt fält"),
        adminUsername: isUpdating ? Yup.string() : Yup.string().required("Admin-användarnamn är obligatoriskt"),
        name: Yup.string().required("Obligatoriskt fält"),
        hsaId: Yup.string().required("Obligatoriskt fält"),
        streetAddress: Yup.string().required("Obligatoriskt fält"),
        zipCode: Yup.string()
            .transform(value => value?.replace(/\s/g, ''))
            .matches(/^\d{5}$/, "Postnummer måste vara 5 siffror")
            .required("Obligatoriskt fält"),
        city: Yup.string().required("Obligatoriskt fält"),
        contactName: Yup.string().required("Obligatoriskt fält"),
        contactPhoneNumber: Yup.string()
            .transform(value => value?.replace(/[\s\-()]/g, ''))
            .matches(/^(?:\+46|0)\d{6,9}$/, "Ogiltigt svenskt telefonnummer")
            .required("Obligatoriskt fält"),
        cosClientId: Yup.string().required("Obligatoriskt fält"),
        cosClientSecret: Yup.string().required("Obligatoriskt fält"),
        cosTokenEndpointUrl: Yup.string().required("Obligatoriskt fält"),
        cosReferralApiUrl: Yup.string().required("Obligatoriskt fält"),
        partnerApiSubscriptionKey: Yup.string().required("Obligatoriskt fält"),
        scope: Yup.string().required("Obligatoriskt fält"),
        referralPrice: Yup.number().required("Obligatoriskt fält").positive("Måste vara ett positivt tal"),
        clinicInfo: Yup.string(),
        externalUserId: Yup.string(),
    });

    return (
        <Modal show={true} onHide={onCancelClicked} backdrop="static" keyboard={true} size="lg">
            <div className="SaveCareUnitDialog">
                <Modal.Header closeButton>
                    <ModalTitle>
                        <h3 className="mb-0">{isUpdating ? "Uppdatera vårdenhet" : "Skapa vårdenhet"}</h3>
                    </ModalTitle>
                </Modal.Header>
                <Modal.Body>
                    <Formik
                        initialValues={{
                            organizationEmail: careUnitForm.organizationEmail || "",
                            adminEmail: careUnitForm.adminEmail || "",
                            adminEmailRepetition: careUnitForm.adminEmailRepetition || "",
                            adminUsername: careUnitForm.adminUsername || "",
                            name: careUnitForm.name || "",
                            hsaId: careUnitForm.hsaId || "",
                            streetAddress: careUnitForm.streetAddress || "",
                            zipCode: careUnitForm.zipCode || "",
                            city: careUnitForm.city || "",
                            contactName: careUnitForm.contactName || "",
                            contactPhoneNumber: careUnitForm.contactPhoneNumber || "",
                            cosClientId: careUnitForm.cosClientId || "",
                            cosClientSecret: careUnitForm.cosClientSecret || "",
                            cosTokenEndpointUrl: careUnitForm.cosTokenEndpointUrl || "",
                            cosReferralApiUrl: careUnitForm.cosReferralApiUrl || "",
                            partnerApiSubscriptionKey: careUnitForm.partnerApiSubscriptionKey || "",
                            scope: careUnitForm.scope || "",
                            referralPrice: careUnitForm.referralPrice || "",
                            clinicInfo: careUnitForm.clinicInfo || "",
                            externalUserId: careUnitForm.externalUserId || "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                            const sanitizedValues = {
                                ...values,
                                clinicInfo: sanitizeHtml(values.clinicInfo),
                            };
                            if (onUpdateClicked) {
                                const {adminEmail, adminEmailRepetition, ...updateData} = sanitizedValues;
                                onUpdateClicked({
                                    ...updateData,
                                    organizationId: careUnitForm.organizationId
                                });
                            } else {
                                onSaveClicked(sanitizedValues);
                            }
                        }}
                    >
                        {({isValid, touched, errors, values, setFieldValue}) => (
                            <Form className="SaveCareUnitForm">
                                <fieldset disabled={isLoading}>
                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Vårdenhetens namn</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="name"
                                                   placeholder="Ex: Ögonkliniken Stockholm"/>
                                            <ErrorMessage name="name" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>HSA-id</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="hsaId"
                                                   placeholder="Ex: SE12345678901234"/>
                                            <ErrorMessage name="hsaId" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Gatuadress</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="streetAddress"
                                                   placeholder="Ex: Sjukhusvägen 15B"/>
                                            <ErrorMessage name="streetAddress" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Postnummer / Stad</FormLabel>
                                        <Col sm={4}>
                                            <Field as={FormControl} type="text" name="zipCode"
                                                   placeholder="Ex: 123 45"/>
                                            <ErrorMessage name="zipCode" component="div" className="text-danger small"/>
                                        </Col>
                                        <Col sm={4}>
                                            <Field as={FormControl} type="text" name="city"
                                                   placeholder="Ex: Stockholm"/>
                                            <ErrorMessage name="city" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Organisations-email</FormLabel>
                                        <Col sm={8}>
                                            <InputGroup>
                                                <InputGroup.Text className="SaveCareUnitDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                <Field as={FormControl} type="email" name="organizationEmail"
                                                       placeholder="Ex: info@vardenhet.se"/>
                                            </InputGroup>
                                            <ErrorMessage name="organizationEmail" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Kontaktperson</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="contactName"
                                                   placeholder="För- och efternamn"/>
                                            <ErrorMessage name="contactName" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Telefonnummer</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="contactPhoneNumber"
                                                   placeholder="Ex: 0701234567"/>
                                            <ErrorMessage name="contactPhoneNumber" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Referral Price</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="number" name="referralPrice"
                                                   placeholder="Ex: 500"/>
                                            <ErrorMessage name="referralPrice" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <Accordion flush className="SaveCareUnitDialog-clinicinfo-accordion">
                                        <Accordion.Item eventKey="0">
                                            <FormGroup as={Row} className="align-items-center">
                                                <FormLabel column sm={4}>Klinikinformation <span className="text-muted fw-normal">(valfritt)</span></FormLabel>
                                                <Col sm={8}>
                                                    <Accordion.Header>
                                                        Visa / redigera
                                                    </Accordion.Header>
                                                </Col>
                                            </FormGroup>
                                            <Accordion.Body>
                                                <TextEditor
                                                    value={values.clinicInfo}
                                                    onChange={(html) => setFieldValue('clinicInfo', html)}
                                                    placeholder="Klinikspecifik information till optiker..."
                                                    rows={8}
                                                />
                                                <ErrorMessage name="clinicInfo" component="div" className="text-danger small"/>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>

                                    <div className="SaveCareUnitDialog-section">COS-integration</div>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>COS Client-ID</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="cosClientId"
                                                   placeholder="Ex: 12345678-1234-1234-12345678"/>
                                            <ErrorMessage name="cosClientId" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>COS Client-Secret</FormLabel>
                                        <Col sm={8}>
                                            <div className="SaveCareUnitDialog-secret-field">
                                                <Field
                                                    as={FormControl}
                                                    type={showPassword ? "text" : "password"}
                                                    name="cosClientSecret"
                                                    placeholder="Ex: 123321-123"
                                                />
                                                <Button onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <EyeSlashFill/> : <Eye/>}
                                                </Button>
                                            </div>
                                            <ErrorMessage name="cosClientSecret" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>COS Token Endpoint URL</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="cosTokenEndpointUrl"
                                                   placeholder="Ex: /COSTokenEndpoint"/>
                                            <ErrorMessage name="cosTokenEndpointUrl" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>COS Referral API URL</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="cosReferralApiUrl"
                                                   placeholder="Ex: CareUnitUnited.se/api/referral"/>
                                            <ErrorMessage name="cosReferralApiUrl" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Partner API Subscription Key</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="partnerApiSubscriptionKey"
                                                   placeholder="Ex: qwerty123ytrewq321"/>
                                            <ErrorMessage name="partnerApiSubscriptionKey" component="div"
                                                          className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                        <FormLabel column sm={4}>Scope</FormLabel>
                                        <Col sm={8}>
                                            <Field as={FormControl} type="text" name="scope"
                                                   placeholder="Ex: ACOG"/>
                                            <ErrorMessage name="scope" component="div" className="text-danger small"/>
                                        </Col>
                                    </FormGroup>

                                    {!isUpdating && (
                                        <>
                                            <div className="SaveCareUnitDialog-section">Admin-användare</div>

                                            <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                                <FormLabel column sm={4}>Externt användar-ID <span className="text-muted fw-normal">(valfritt)</span></FormLabel>
                                                <Col sm={8}>
                                                    <Field as={FormControl} type="text" name="externalUserId"
                                                           placeholder="Externt användar-ID"/>
                                                    <ErrorMessage name="externalUserId" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                                <FormLabel column sm={4}>Admin-email</FormLabel>
                                                <Col sm={8}>
                                                    <InputGroup>
                                                        <InputGroup.Text className="SaveCareUnitDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                        <Field as={FormControl} type="email" name="adminEmail"
                                                               placeholder="Email för admin-användare"/>
                                                    </InputGroup>
                                                    <ErrorMessage name="adminEmail" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                                <FormLabel column sm={4}>Repetera admin-email</FormLabel>
                                                <Col sm={8}>
                                                    <InputGroup>
                                                        <InputGroup.Text className="SaveCareUnitDialog-input-icon"><BsEnvelope/></InputGroup.Text>
                                                        <Field as={FormControl} type="email" name="adminEmailRepetition"
                                                               placeholder="Repetera email"/>
                                                    </InputGroup>
                                                    <ErrorMessage name="adminEmailRepetition" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup as={Row} className="SaveCareUnitDialog-row align-items-center">
                                                <FormLabel column sm={4}>Admin-användarnamn</FormLabel>
                                                <Col sm={8}>
                                                    <Field as={FormControl} type="text" name="adminUsername"
                                                           placeholder="Användarnamn för admin-användare"/>
                                                    <ErrorMessage name="adminUsername" component="div"
                                                                  className="text-danger small"/>
                                                </Col>
                                            </FormGroup>
                                        </>
                                    )}
                                </fieldset>

                                {saveErrorMessage &&
                                    <Alert variant="danger" className="mt-2">{saveErrorMessage}</Alert>}

                                {/* Footer */}
                                <div className="SaveCareUnitDialog-footer">
                                    {confirmAction === 'delete' && (
                                        <>
                                            <div className="SaveCareUnitDialog-footer-message">
                                                Vill du ta bort vårdenheten <strong>{careUnitForm.name}</strong>? Detta kan
                                                inte ångras.
                                            </div>
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button variant="danger" disabled={isLoading}
                                                        onClick={() => setConfirmAction(null)}>
                                                    Avbryt
                                                </Button>
                                                <Button variant="primary" disabled={isLoading}
                                                        onClick={() => onDeleteClicked({organizationId: careUnitForm.organizationId})}>
                                                    {isLoading ? <><Spinner animation="border" size="sm"/> Tar
                                                        bort...</> : <><BsTrash style={{marginRight: '6px'}}/>Ta bort
                                                        vårdenhet</>}
                                                </Button>
                                            </div>
                                        </>
                                    )}

                                    {!confirmAction && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex gap-3">
                                                {isUpdating && isAdminRole && onDeleteClicked && (
                                                    <Button
                                                        variant="link"
                                                        className="SaveCareUnitDialog-link-btn text-danger"
                                                        onClick={() => setConfirmAction('delete')}
                                                        disabled={isLoading}
                                                    >
                                                        <BsTrash style={{marginRight: '5px'}}/>Ta bort
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="danger"
                                                    onClick={onCancelClicked}
                                                    disabled={isLoading}
                                                >
                                                    Avbryt
                                                </Button>
                                                {onUpdateClicked ? (
                                                    <Button type="submit" variant="primary" disabled={isLoading}>
                                                        {isLoading ? <><Spinner animation="border"
                                                                                size="sm"/> Uppdaterar...</> : "Uppdatera"}
                                                    </Button>
                                                ) : (
                                                    <Button type="submit" variant="primary" disabled={isLoading}>
                                                        {isLoading ? <><Spinner animation="border"
                                                                                size="sm"/> Skapar...</> : <><BsPlusLg
                                                            style={{marginRight: '6px'}}/>Skapa</>}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </div>
        </Modal>
    );
};
export default SaveCareUnitDialog;